import { cn } from '@/lib/utils'
import type { PanelArtProps } from './panel-art-props'

/**
 * Chart Scanner / History Chart Grid art (one drawing for both): a 2x2 grid of
 * intraday lines with few angles: a quiet chop in a muted tone, then the move
 * in the run colour. Top left a strong green runner, top right a quiet grey
 * tape, bottom left an early spike, bottom right a downtrend. No markers, no
 * indicator lines.
 */

interface Cell {
  /** The pre-breakout chop, small and irregular. */
  quiet: string
  /** From the last quiet point: the break and the swings after it. */
  run: string
  /** Cell floor for the soft area under the run. */
  baseline: number
  runX: [number, number]
  tone: string
}

const CELLS: Cell[] = [
  {
    // Top left: a strong green runner that breaks out and holds high.
    quiet: 'M14 42 L20 41 L27 43 L33 40 L40 42 L46 40',
    run: 'M46 40 L50 22 L55 26 L61 18 L67 21 L73 17 L78 19',
    baseline: 47,
    runX: [46, 78],
    tone: 'hsl(var(--chart-candle-up))',
  },
  {
    // Top right: a quiet tape, grey, drifting with one small lift.
    quiet: 'M82 40 L90 41 L98 39 L106 41 L114 38 L122 40',
    run: 'M122 40 L128 34 L135 36 L141 31 L146 33',
    baseline: 47,
    runX: [122, 146],
    tone: 'hsl(var(--muted-foreground) / 0.75)',
  },
  {
    // Bottom left: an early spike, then a settled, choppy hold.
    quiet: 'M14 80 L18 81 L22 79',
    run: 'M22 79 L26 60 L31 64 L38 58 L46 63 L54 60 L62 65 L70 61 L78 64',
    baseline: 87,
    runX: [22, 78],
    tone: 'hsl(var(--primary))',
  },
  {
    // Bottom right: a downtrend, lower highs and lower lows into the close.
    quiet: 'M82 60 L88 58 L94 62',
    run: 'M94 62 L100 66 L107 63 L114 71 L121 68 L129 76 L137 74 L146 81',
    baseline: 87,
    runX: [94, 146],
    tone: 'hsl(var(--chart-candle-down))',
  },
]

function areaPath(run: string, baseline: number, [fromX, toX]: [number, number]): string {
  return `${run} L${toX} ${baseline} L${fromX} ${baseline} Z`
}

export function MultiChartArt({ className }: PanelArtProps) {
  return (
    <svg
      viewBox="0 0 160 100"
      preserveAspectRatio="xMidYMid meet"
      aria-hidden="true"
      focusable="false"
      className={cn('h-full w-full', className)}
    >
      <path
        d="M80 12 V88 M12 50 H148"
        fill="none"
        stroke="hsl(var(--muted-foreground) / 0.25)"
        strokeWidth="1.5"
        vectorEffect="non-scaling-stroke"
      />
      {CELLS.map((cell) => (
        <g key={cell.runX.join('-')}>
          <path
            d={areaPath(cell.run, cell.baseline, cell.runX)}
            fill={
              cell.tone.includes(' / ')
                ? cell.tone.replace(/ \/ [\d.]+\)/, ' / 0.1)')
                : cell.tone.replace('))', ') / 0.12)')
            }
            stroke="none"
          />
          <path
            d={cell.quiet}
            fill="none"
            stroke="hsl(var(--muted-foreground) / 0.7)"
            strokeWidth="1.75"
            strokeLinecap="round"
            strokeLinejoin="round"
            vectorEffect="non-scaling-stroke"
          />
          <path
            d={cell.run}
            fill="none"
            stroke={cell.tone}
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            vectorEffect="non-scaling-stroke"
          />
        </g>
      ))}
    </svg>
  )
}
